// Dialog de troca de marca — compartilhado entre a sidebar (desktop) e o drawer (mobile).
// Diretor enxerga TODAS as marcas via GET /v1/brands; clicar numa troca na hora e,
// se ele ainda não tiver acesso, cria o membership antes de ativar. Para os demais
// papéis, a consulta cross-org nem é feita: a lista vem apenas das filiações do usuário.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2, Check, Plus, RotateCcw, Trash2 } from "lucide-react";
import { invalidateAppAccess, useActiveOrganization } from "@/lib/auth-client";
import { BrandDialogSkeleton } from "@/components/screen-skeletons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { BrandSummary } from "@/api/brands";
import { BrandCreateDialog } from "@/brand-create-dialog";
import {
  useAllBrands,
  useCreateBrand,
  useMyBrands,
  usePurgeBrand,
  useReactivateBrand,
  useSwitchToBrand,
} from "@/hooks/use-brands";
import { useCanCreateBrand, useCrossBrand } from "@/hooks/use-viewer-role";
import "./i18n.js";

export function BrandDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Quem vê marca alheia é o diretor de PLATAFORMA de um cliente do tipo
  // 'grupo'. Para o cliente 'unica' a listagem cross-org não é só negada pelo
  // back (403): ela não teria o que listar além da única marca dele, que já
  // vem por /brands/mine.
  const crossBrand = useCrossBrand();
  const canCreateBrand = useCanCreateBrand();
  const { data: activeOrg, refetch: refetchActiveOrg } = useActiveOrganization();
  const { data: memberBrands, isLoading: isLoadingMemberBrands } = useMyBrands({ enabled: open });
  const { data: allBrands, isLoading: isLoadingAllBrands } = useAllBrands({
    enabled: open && crossBrand,
  });
  const { mutateAsync: reactivate } = useReactivateBrand();
  const { mutateAsync: purge } = usePurgeBrand();
  const { mutateAsync: create } = useCreateBrand();
  const { mutateAsync: switchBrand } = useSwitchToBrand();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Marca em vias de ser apagada DE VEZ, e o nome digitado para confirmar. O
  // texto é a trava: a ação não tem desfazer, então não pode caber num clique.
  const [apagando, setApagando] = useState<BrandSummary | null>(null);
  const [confirmacao, setConfirmacao] = useState("");
  const confirmado = apagando !== null && confirmacao.trim() === apagando.name;

  // Marca desativada (DELETE /v1/brands/:id) só chega aqui na listagem cross-org
  // do diretor de plataforma: /brands/mine já não a devolve. Ela não é
  // selecionável — o join recusaria com 409 —, mas é daqui que sai a reversão,
  // porque depois da desativação não existe mais um diretor da marca para pedi-la.
  async function handleReactivate(brand: BrandSummary) {
    if (busyId) return;
    setBusyId(brand.id);
    try {
      // O hook já reativa e derruba o cache inteiro (ver use-brands.ts).
      await reactivate(brand.id);
      toast.success(t("brandDialog.reactivated", { name: brand.name }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("brandDialog.reactivateError"));
    } finally {
      setBusyId(null);
    }
  }

  // Exclusão DEFINITIVA, disponível só na linha da marca já excluída: a
  // desativação é a rede de segurança, e o back recusa marca no ar com 409.
  // Aqui a marca some do banco — leads, ligações, gravações, filas e chaves —
  // e não há reversão, ao contrário do handleReactivate acima.
  async function handlePurge(brand: BrandSummary) {
    if (busyId) return;
    setBusyId(brand.id);
    try {
      const out = await purge(brand.id);
      setApagando(null);
      setConfirmacao("");
      toast.success(t("brandDialog.purged", { name: brand.name }), {
        description: t("brandDialog.purgedDescription", { count: out.total }),
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("brandDialog.purgeError"));
    } finally {
      setBusyId(null);
    }
  }

  // Defesa em profundidade: mesmo uma resposta cross-org inesperada nunca é
  // usada por quem não tem acesso cross-marca.
  const canSeeAllBrands = crossBrand && Array.isArray(allBrands);
  const brands = canSeeAllBrands ? allBrands : (memberBrands ?? []);
  const isLoading = isLoadingMemberBrands || (crossBrand && isLoadingAllBrands);
  const activeId = activeOrg?.id ?? "";

  // Chamado pelo BrandCreateDialog com o nome já validado. O erro é engolido
  // aqui de propósito: vira toast e o dialog de criação segue aberto com o que
  // foi digitado, em vez de fechar e perder o texto.
  async function handleCreate(name: string) {
    if (busyId) return;

    setBusyId("create-brand");
    try {
      const brand = await create(name);
      // O back já grava o membership. Definir a nova marca como ativa deixa o
      // fluxo pronto para o usuário começar a configurá-la imediatamente.
      await switchBrand(brand);
      invalidateAppAccess();
      await queryClient.invalidateQueries();
      await refetchActiveOrg();
      setCreating(false);
      toast.success(t("brandDialog.created", { name: brand.name }));
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("brandDialog.createError"));
    } finally {
      setBusyId(null);
    }
  }

  async function handleSelect(brand: BrandSummary) {
    if (busyId) return;
    if (brand.id === activeId) {
      onOpenChange(false);
      return;
    }
    setBusyId(brand.id);
    try {
      await switchBrand(brand);
      invalidateAppAccess();
      // A UI usa somente a organização ativa e as listas escopadas do nosso
      // /v1/brands. Não consulta a listagem genérica do Better Auth, que não
      // conhece a fronteira de company.
      await refetchActiveOrg();
      // Novo acesso muda isMember; dados servidos por-org precisam recarregar.
      await queryClient.invalidateQueries();
      toast.success(t("brandDialog.activeToast", { name: brand.name }));
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("brandDialog.switchError"));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("brandDialog.title")}</DialogTitle>
          <DialogDescription>
            {canSeeAllBrands ? t("brandDialog.allDescription") : t("brandDialog.ownDescription")}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <BrandDialogSkeleton />
        ) : brands.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("brandDialog.empty")}</p>
        ) : (
          <ScrollArea className="max-h-[60vh]">
            <div className="flex flex-col gap-1 pr-3">
              {brands.map((brand) => {
                const isActive = brand.id === activeId;
                // Linha da marca desativada: não é botão de troca — entrar nela
                // é 409 no back. O que sobra é reativar.
                if (brand.deactivatedAt) {
                  return (
                    <div
                      key={brand.id}
                      className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-muted-foreground"
                    >
                      <Building2 className="size-4 shrink-0" />
                      <span className="flex-1 truncate line-through">{brand.name}</span>
                      {busyId === brand.id ? (
                        <Spinner className="size-4" />
                      ) : (
                        <>
                          <Badge variant="outline" className="text-muted-foreground">
                            {t("brandDialog.deleted")}
                          </Badge>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={busyId !== null}
                            onClick={() => void handleReactivate(brand)}
                          >
                            <RotateCcw className="size-3" />
                            {t("brandDialog.reactivate")}
                          </Button>
                          <Button
                            type="button"
                            size="icon-sm"
                            variant="ghost"
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                            title={t("brandDialog.purgeTitle")}
                            aria-label={t("brandDialog.purgeAria", { name: brand.name })}
                            disabled={busyId !== null}
                            onClick={() => {
                              setConfirmacao("");
                              setApagando(brand);
                            }}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  );
                }
                return (
                  <button
                    key={brand.id}
                    type="button"
                    disabled={busyId !== null}
                    onClick={() => void handleSelect(brand)}
                    className="flex items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-accent disabled:opacity-50"
                  >
                    <Building2 className="size-4 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">{brand.name}</span>
                    {busyId === brand.id ? (
                      <Spinner className="size-4" />
                    ) : isActive ? (
                      <Badge variant="secondary" className="gap-1">
                        <Check className="size-3" /> {t("brandDialog.active")}
                      </Badge>
                    ) : !brand.isMember ? (
                      <Badge variant="outline" className="gap-1 text-muted-foreground">
                        <Plus className="size-3" /> {t("brandDialog.createAccess")}
                      </Badge>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        )}

        {canCreateBrand && !isLoading ? (
          <button
            type="button"
            disabled={busyId !== null}
            onClick={() => setCreating(true)}
            className="flex items-center gap-2 rounded-md border border-dashed px-2 py-2 text-left text-sm text-muted-foreground hover:border-solid hover:bg-accent hover:text-foreground disabled:opacity-50"
          >
            <Plus className="size-4 shrink-0" />
            <span className="flex-1 truncate">{t("brandDialog.create")}</span>
          </button>
        ) : null}
      </DialogContent>

      <BrandCreateDialog
        open={creating}
        onOpenChange={setCreating}
        busy={busyId !== null}
        onSubmit={handleCreate}
      />

      <AlertDialog
        open={apagando !== null}
        onOpenChange={(aberto) => {
          if (!aberto && busyId === null) {
            setApagando(null);
            setConfirmacao("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("brandDialog.confirmationTitle", { name: apagando?.name })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("brandDialog.confirmationBefore")} <strong>{t("brandDialog.everything")}</strong>
              {t("brandDialog.confirmationAfter")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="confirmar-nome-marca">
              {t("brandDialog.typeBefore")}{" "}
              <span className="font-medium text-foreground">{apagando?.name}</span>{" "}
              {t("brandDialog.typeAfter")}
            </Label>
            <Input
              id="confirmar-nome-marca"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              autoComplete="off"
              disabled={busyId !== null}
              placeholder={apagando?.name}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busyId !== null}>
              {t("brandDialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={!confirmado || busyId !== null}
              onClick={(e) => {
                // O Radix fecha no clique; aqui o fechamento é do handler, para
                // o dialog não sumir antes da resposta do back.
                e.preventDefault();
                if (apagando) void handlePurge(apagando);
              }}
            >
              {busyId !== null ? <Spinner className="size-4" /> : <Trash2 className="size-4" />}
              {t("brandDialog.purgeForever")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
